import React, { useRef, useState, useEffect } from 'react';
import { Navbar, Nav, Container } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import Loader from './Loader';
import Message from './Message';
import { useGetFontProductsQuery, useGetProductsNavQuery, useGetProductTypesQuery } from '../slices/productsApiSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import {
    faAlbumCollection, faBadgeSheriff, faBoxOpen, faCards, faDuck, faFrame, faImage, faJoystick, faKitchenSet,
    faLampDesk, faMugHot, faQuestionCircle, faShirt, faUserBountyHunter,
} from '@awesome.me/kit-367a27fd96/icons/classic/light';
import { faHouse, faChevronLeft, faChevronRight, faGalleryThumbnails } from '@awesome.me/kit-367a27fd96/icons/classic/light';

// Importing the whole icon set added about 3MB for every visitor.
// A category given a new icon in admin must be added here, or it shows a question mark.
const NAV_ICONS = {
    faAlbumCollection, faBadgeSheriff, faBoxOpen, faCards, faDuck, faFrame, faImage, faJoystick, faKitchenSet,
    faLampDesk, faMugHot, faShirt, faUserBountyHunter,
};

const HeaderNav = ({ onProductSelect, clearHomeScreen }) => {
    const navigate = useNavigate();
    const { data: fontProducts, isLoading: isFontLoading, error: fontError } = useGetProductTypesQuery();
    const { data: allProducts, isLoading: isAllLoading, error: allError } = useGetProductsNavQuery({ keyword: '', pageNumber: 1 });
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    // Drag-to-scroll state
    const scrollRef = useRef(null);
    const isDraggingRef = useRef(false);
    const hasDraggedRef = useRef(false);
    const startXRef = useRef(0);
    const scrollLeftRef = useRef(0);

    // Momentum scroll state
    const velocityRef = useRef(0);
    const lastTimeRef = useRef(0);
    const lastXRef = useRef(0);
    const animationFrameRef = useRef(null);

    const HOVER_SCROLL_ENABLED = false;
    const DRAG_THRESHOLD = 10;
    const scrollInterval = useRef(null);

    // Handle icon click
    const handleIconClick = (keyword, updateDescription, e) => {
        if (hasDraggedRef.current) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        navigate({ pathname: '/search/', search: `?productType=${keyword}` });
        onProductSelect(updateDescription);
    };

    // Start dragging with momentum tracking
    const startDragging = (e) => {
        const slider = scrollRef.current;
        if (!slider) return;

        // Cancel any ongoing momentum
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }

        isDraggingRef.current = true;
        hasDraggedRef.current = false;
        slider.style.cursor = 'grabbing';
        slider.style.userSelect = 'none';

        const pageX = e.pageX || (e.touches && e.touches[0]?.pageX);
        startXRef.current = pageX - slider.offsetLeft;
        scrollLeftRef.current = slider.scrollLeft;
        lastXRef.current = pageX;
        lastTimeRef.current = Date.now();
        velocityRef.current = 0;
    };

    // While dragging - calculate velocity
    const onDragging = (e) => {
        if (!isDraggingRef.current) return;
        e.preventDefault();

        const slider = scrollRef.current;
        if (!slider) return;

        const pageX = e.pageX || (e.touches && e.touches[0]?.pageX);
        const x = pageX - slider.offsetLeft;
        const walk = (x - startXRef.current);

        // Calculate velocity for momentum
        const currentTime = Date.now();
        const timeDelta = currentTime - lastTimeRef.current;
        const xDelta = pageX - lastXRef.current;

        if (timeDelta > 0) {
            velocityRef.current = xDelta / timeDelta;
        }

        lastXRef.current = pageX;
        lastTimeRef.current = currentTime;

        // Only count as drag if moved more than threshold
        if (Math.abs(walk) > DRAG_THRESHOLD) {
            hasDraggedRef.current = true;
            slider.scrollLeft = scrollLeftRef.current - walk;
        }
    };

    // Stop dragging - apply momentum
    const stopDragging = () => {
        const slider = scrollRef.current;
        isDraggingRef.current = false;

        if (slider) {
            slider.style.cursor = 'grab';
            slider.style.userSelect = 'auto';

            // Apply momentum scroll
            const velocity = velocityRef.current;
            if (Math.abs(velocity) > 0.1) {
                applyMomentum(slider, velocity);
            }
        }

        // Reset hasDragged after a brief delay
        setTimeout(() => {
            hasDraggedRef.current = false;
        }, 100);
    };

    // Momentum scrolling animation change this to improvement the motion onf the scroll
    const applyMomentum = (element, initialVelocity) => {
        let velocity = initialVelocity * -50; // Amplify and reverse direction
        const friction = 0.95; // Deceleration factor
        const minVelocity = 0.5;

        const scroll = () => {
            if (Math.abs(velocity) < minVelocity) {
                animationFrameRef.current = null;
                return;
            }

            element.scrollLeft += velocity;
            velocity *= friction;

            animationFrameRef.current = requestAnimationFrame(scroll);
        };

        scroll();
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isMobile || !scrollRef.current) return;
        if (HOVER_SCROLL_ENABLED) {
            startAutoScroll();
        }
        return () => stopScroll();
    }, [isMobile]);

    // Cleanup animation frame on unmount
    useEffect(() => {
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    const startAutoScroll = () => {
        stopScroll();
        const scrollSpeed = 150;
        const scrollAmount = 1;

        scrollInterval.current = setInterval(() => {
            if (scrollRef.current) {
                scrollRef.current.scrollBy({ left: scrollAmount });
            }
        }, scrollSpeed);
    };

    const stopScroll = () => {
        if (scrollInterval.current) {
            clearInterval(scrollInterval.current);
            scrollInterval.current = null;
        }
    };

    const handleMouseEnter = () => {
        if (HOVER_SCROLL_ENABLED) {
            stopScroll();
        }
    };

    const handleMouseLeave = () => {
        if (HOVER_SCROLL_ENABLED && !isDraggingRef.current) {
            startAutoScroll();
        }
    };

    const scrollLeftBtn = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
        }
    };

    const scrollRightBtn = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
        }
    };

    const renderProducts = (products, onProductSelect) => {
        return products.map((product, index) => {
            const iconName = `${product.font.charAt(0) + product.font.slice(1)}`;
            const IconComponent = NAV_ICONS[iconName] || faQuestionCircle;
            return (
                <LinkContainer
                    key={index}
                    to={{ pathname: '/search/', search: `?productType=${product.productType}` }}
                    onClick={(e) => handleIconClick(product.productType, product.productTypeDescription, e)}
                >
                    <Nav.Link
                        className="icon-link-container"
                        draggable="false"
                        onMouseEnter={HOVER_SCROLL_ENABLED ? handleMouseEnter : null}
                        onMouseLeave={HOVER_SCROLL_ENABLED ? handleMouseLeave : null}
                        aria-label={`Browse ${product.productType}`}
                    >
                        <div className="icon-wrapper" draggable="false">
                            {IconComponent && (
                                <FontAwesomeIcon
                                    icon={IconComponent}
                                    color="black"
                                    className="icon"
                                    draggable="false"
                                    aria-hidden="true"
                                />
                            )}
                            <span className="icon-text" draggable="false">
                                {product.productType}
                            </span>
                        </div>
                    </Nav.Link>
                </LinkContainer>
            );
        });
    };

    if (isFontLoading || isAllLoading) return <Loader />;
    if (fontError || allError) return <Message variant="danger">{fontError || allError}</Message>;

    return (
        <header>
            <Navbar
                className="horizontal-scroll-nav headerNavSpacing"
                style={{ backgroundColor: "#ffffff", height: '100px'}}
            >
                <Container fluid>
                    <Navbar.Toggle className="custom-toggle" aria-controls="basic-navbar-nav" />

                    <div style={{ display: 'flex', width: '100%'}}>
                        <button
                            className="nav-arrow left"
                            onClick={scrollLeftBtn}
                            onMouseEnter={HOVER_SCROLL_ENABLED ? handleMouseEnter : null}
                            onMouseLeave={HOVER_SCROLL_ENABLED ? handleMouseLeave : null}
                            aria-label="Scroll navigation left"
                        >
                            <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
                        </button>

                        <div
                            ref={scrollRef}
                            style={{
                                overflowX: 'auto',
                                flexGrow: 1,
                                cursor: 'grab',
                                WebkitUserDrag: 'none',
                                userSelect: 'none',
                                touchAction: 'pan-x'
                            }}
                            onMouseDown={startDragging}
                            onMouseMove={onDragging}
                            onMouseUp={stopDragging}
                            onMouseLeave={stopDragging}
                            onTouchStart={startDragging}
                            onTouchMove={onDragging}
                            onTouchEnd={stopDragging}
                            onMouseEnter={HOVER_SCROLL_ENABLED ? handleMouseEnter : null}
                            role="navigation"
                            aria-label="Product category navigation"
                        >
                            <nav className="navbar-nav">
                                <LinkContainer
                                    to='/'
                                    onClick={(e) => {
                                        if (hasDraggedRef.current) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            return;
                                        }
                                        clearHomeScreen();
                                    }}
                                >
                                    <Nav.Link
                                        className="icon-link-container"
                                        draggable="false"
                                        onMouseEnter={HOVER_SCROLL_ENABLED ? handleMouseEnter : null}
                                        onMouseLeave={HOVER_SCROLL_ENABLED ? handleMouseLeave : null}
                                        aria-label="Go to home page"
                                    >
                                        <div className="icon-wrapper" draggable="false">
                                            <FontAwesomeIcon
                                                icon={faHouse}
                                                size="2x"
                                                className="icon"
                                                draggable="false"
                                                aria-hidden="true"
                                            />
                                            <span className="icon-text" draggable="false">Home</span>
                                        </div>
                                    </Nav.Link>
                                </LinkContainer>

                                <LinkContainer
                                    to={'/search/'}
                                    onClick={(e) => {
                                        if (hasDraggedRef.current) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            return;
                                        }
                                        onProductSelect('');
                                    }}
                                >
                                    <Nav.Link
                                        className="icon-link-container"
                                        draggable="false"
                                        onMouseEnter={HOVER_SCROLL_ENABLED ? handleMouseEnter : null}
                                        onMouseLeave={HOVER_SCROLL_ENABLED ? handleMouseLeave : null}
                                        aria-label="Browse all items"
                                    >
                                        <div className="icon-wrapper" draggable="false">
                                            <FontAwesomeIcon
                                                icon={faGalleryThumbnails}
                                                size="2x"
                                                className="icon"
                                                draggable="false"
                                                aria-hidden="true"
                                            />
                                            <span className="icon-text" draggable="false">All Items</span>
                                        </div>
                                    </Nav.Link>
                                </LinkContainer>

                                {renderProducts(fontProducts, onProductSelect)}
                            </nav>
                        </div>

                        <button
                            className="nav-arrow right"
                            onClick={scrollRightBtn}
                            onMouseEnter={HOVER_SCROLL_ENABLED ? handleMouseEnter : null}
                            onMouseLeave={HOVER_SCROLL_ENABLED ? handleMouseLeave : null}
                            aria-label="Scroll navigation right"
                        >
                            <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
                        </button>
                    </div>
                </Container>
            </Navbar>
        </header>
    );
};

export default HeaderNav;